import type { ComponentType, ReactNode } from "react";
import { Outlet, type RouteObject } from "react-router-dom";

type PageModule = { default: ComponentType };
type LayoutModule = { default: ComponentType<{ children: ReactNode }> };

function routeSegment(directory: string): string {
  return directory.replace(/^\[(\w+)\]$/, ":$1");
}

// Retain the page/layout directory convention while Vite splits each route into a lazy chunk.
export function createVoidpulseRoutes(
  pages: Record<string, () => Promise<PageModule>>,
  layouts: Record<string, () => Promise<LayoutModule>>,
): RouteObject[] {
  const root: RouteObject = { children: [] };
  const directories = new Map<string, RouteObject>([["", root]]);
  function directory(path: string): RouteObject {
    const existing = directories.get(path);
    if (existing) return existing;
    const segments = path.split("/");
    const segment = segments.pop()!;
    const parent = directory(segments.join("/"));
    const route: RouteObject = { path: routeSegment(segment), children: [] };
    directories.set(path, route);
    parent.children!.push(route);
    return route;
  }
  for (const [file, load] of Object.entries(layouts)) {
    const path = file.replace(/^\.\/app\//, "").replace(/\/?layout\.tsx$/, "");
    if (!path) continue;
    directory(path).lazy = async () => {
      const { default: Layout } = await load();
      return {
        Component: function RouteLayout() {
          return (
            <Layout>
              <Outlet />
            </Layout>
          );
        },
      };
    };
  }
  for (const [file, load] of Object.entries(pages)) {
    const path = file.replace(/^\.\/app\//, "").replace(/\/?page\.tsx$/, "");
    directory(path).children!.push({ index: true, lazy: async () => ({ Component: (await load()).default }) });
  }
  return root.children!;
}
